import {
    type Dispatch,
    type SetStateAction,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";
import { useLocation } from "react-router";
import { useNavigate } from "react-router-dom-v5-compat";

import { BdsIconFunnel, BdsRollupButton } from "@buildertrend/components";

import { FilterValueContext } from "helpers/globalContext/FilterValueContext";

import { track } from "utilities/analytics/analytics";
import { arrayIsEqualDeep } from "utilities/array/array";
import { useHistoryState } from "utilities/historyState/useHistoryState";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTIconFilter } from "commonComponents/btWrappers/BTIcon";

import {
    Filter,
    FilterUnmanaged,
    type IFilterProps,
    type IFilterUnmanagedProps,
} from "entity/filters/Filter/Filter";
import {
    type FilterEntity,
    type FilterEntityType,
    getSelectedFilterItemsFromSavedFilter,
    type IFilterFormValues,
    type SelectedFilterItem,
} from "entity/filters/Filter/Filter.api.types";
import {
    defaultFilterState,
    FilterReducer,
    type IFilterHistoryState,
    type IFilterReducerState,
    SetUnsavedAppliedFilter,
    UpdateUnsavedChanges,
} from "entity/filters/Filter/Filter.reducer";
import { FilterCountDisplay, FilterCountProvider } from "entity/filters/Filter/FilterCount";
import { type IFilterDrawerProps } from "entity/filters/Filter/FilterDrawer.types";
import { useFilterHistoryCount } from "entity/filters/Filter/useFilterHistoryState";
import { applySerializeBehavior, flattenTreeData } from "entity/filters/filters.utils";
import { type SavedFilterItem } from "entity/filters/SavedFilter/SavedFilter.api.types";

import "./FilterDrawer.less";

type IFilterHistoryStateWithType = IFilterHistoryState & {
    filterType: FilterEntityType;
};

export interface IFilterDrawerHistoryState {
    filterState?: IFilterHistoryStateWithType;
}

type UnmanagedFilterDrawerProps = IFilterDrawerProps & IFilterUnmanagedProps;

function isUnmanagedFilterDrawerProps(
    props: UnmanagedFilterDrawerProps | IFilterDrawerProps,
): props is UnmanagedFilterDrawerProps {
    return !!(props as UnmanagedFilterDrawerProps).updateUnsavedChanges;
}

/**
 * @deprecated Use FilterDrawerSimple or FilterDrawerHistory instead
 */
export const FilterDrawer: React.FC<IFilterDrawerProps | UnmanagedFilterDrawerProps> = track({
    component: "Filter Drawer",
})((props) => {
    const openState: [boolean, Dispatch<SetStateAction<boolean>>] =
        props.open === undefined && props.setOpen === undefined
            ? useState(false as boolean)
            : [!!props.open, props.setOpen || ((() => {}) as Dispatch<SetStateAction<boolean>>)];
    const [open, setOpen] = openState;

    const filterContext = useContext(FilterValueContext);

    const appliedType = filterContext?.appliedFilter?.type;
    if (appliedType !== undefined && appliedType !== props.filterTypeEntity) {
        filterContext?.setAppliedFilter(undefined);
        return null;
    }

    let label: React.ReactNode;
    if (!props.iconOnly) {
        label = (
            <>
                Filter{" "}
                {props.appliedFilterCount !== undefined && props.appliedFilterCount > 0 ? (
                    ` (${props.appliedFilterCount})`
                ) : (
                    <FilterCountDisplay />
                )}
            </>
        );
    }

    const renderOldButton = () => {
        return props.useBdsRollupButton ? (
            <BdsRollupButton
                id="filters"
                testid="filters"
                ariaLabel="Filters"
                icon={<BdsIconFunnel />}
                text={props.text}
                onClick={() => {
                    setOpen(!open);
                }}
                value="filter"
                maxStage={props.maxStage}
                minStage={props.minStage}
            />
        ) : (
            <BTButtonDeprecated
                type={props.iconOnly ? "link" : "secondary"}
                htmlType={props.iconOnly ? "button" : undefined}
                data-testid="filters"
                icon={<BTIconFilter />}
                onClick={() => setOpen(!open)}
                className="FilterDrawerButton"
                disabled={props.disabled}
            >
                {label}
            </BTButtonDeprecated>
        );
    };

    const renderButton = () =>
        props.useNewBTButton ? (
            <BTButton
                type={props.iconOnly ? "tertiary" : "secondary"}
                htmlType={props.iconOnly ? "button" : undefined}
                data-testid="filters"
                icon={<BTIconFilter />}
                onClick={() => setOpen(!open)}
                className="FilterDrawerButton"
                disabled={props.disabled}
            >
                {label}
            </BTButton>
        ) : (
            renderOldButton()
        );

    const renderedFilter = (
        <>
            {!props.setOpen || props.useBdsRollupButton ? renderButton() : null}
            {isUnmanagedFilterDrawerProps(props) ? (
                <FilterUnmanaged {...props} useDrawer drawerOpen={open} setDrawerOpen={setOpen} />
            ) : (
                <Filter {...props} useDrawer drawerOpen={open} setDrawerOpen={setOpen} />
            )}
        </>
    );

    if (props.appliedFilterCount !== undefined) {
        return renderedFilter;
    }

    return <FilterCountProvider>{renderedFilter}</FilterCountProvider>;
});

const appliedFiltersNotFromSavedFilter = (
    appliedFilters: SelectedFilterItem[],
    filterEntity: FilterEntity,
    selectedSavedFilter?: SavedFilterItem,
) => {
    return !arrayIsEqualDeep(
        appliedFilters.map((v) => v.selectedValue),
        getSelectedFilterItemsFromSavedFilter(filterEntity, selectedSavedFilter).map(
            (v) => v.selectedValue,
        ),
    );
};

export type IFilterDrawerHistoryProps = Omit<
    IFilterDrawerProps,
    | "onSubmit"
    | "appliedFilter"
    | "updateUnsavedChanges"
    | "setUnsavedAppliedFilter"
    | "showHasUnsavedChanges"
    | "appliedFiltersFromSavedFilter"
    | "shouldUpdateFilterValueContext"
    | "selectedFilterId"
    | "onSavedFiltersUpdated"
    | "appliedFilterCount"
> & {
    onReloadEntity: (id?: number) => Promise<void>;
};

/**
 * This is filter drawer is designed for a full page experience (list pages, full page detail views etc).
 * @example
 *
 * const state = useHistoryState<IDailyLogListHistoryState>({});
 * const filters = useFilterHistoryState(state, filterTypeEntity, props.jobIds, props.systemFilter);
 * if (filters.isLoading) {
 *     return <BTLoading />
 * }
 *
 * return <FilterDrawerHistory entity={filters.filterEntity} onReloadEntity={filters.reload}/>;
 */
export const FilterDrawerHistory = (props: IFilterDrawerHistoryProps) => {
    const location = useLocation<IFilterDrawerHistoryState>();
    const navigate = useNavigate();

    const { filterTypeEntity } = props;

    const onChange = useCallback(
        (state: IFilterHistoryState) => {
            const historyValues: IFilterHistoryStateWithType = {
                ...state,
                filterType: props.filterTypeEntity,
            };

            navigate(location.pathname + location.search, {
                replace: true,
                state: {
                    ...(typeof location.state === "object" ? location.state : {}),
                    filterState: historyValues,
                },
            });
        },
        [navigate, location, props.filterTypeEntity],
    );

    const historyState = useHistoryState<IFilterDrawerHistoryState>({});

    const state = useMemo(() => {
        const filters = getFiltersFromHistory(historyState, filterTypeEntity, defaultFilterState);

        return filters;
    }, [historyState, filterTypeEntity]);

    const filterHistoryCount = useFilterHistoryCount(props.entity, state);

    return (
        <FilterDrawerSimple
            {...props}
            state={state}
            onChange={onChange}
            appliedFilterCount={filterHistoryCount}
        />
    );
};

type IFilterDrawerSimpleProps = IFilterDrawerHistoryProps & {
    onChange: (state: IFilterHistoryState) => void;
    state: IFilterHistoryState;
    appliedFilterCount?: number;
};

/**
 * This filter drawer is intended for use in a modal or panel.
 * @example
 *
 * const [filterState, setFilterState] = useState<IFilterHistoryState | undefined>();
 * const filters = useFilterState(filterState, filterTypeEntity, setFilterState, props.jobIds, props.systemFilter);
 * if (filters.isLoading) {
 *     return <BTLoading />
 * }
 *
 * return <FilterDrawerSimple {...props} state={filters.filterState} onChange={setFilterState} entity={filters.filterEntity} onReloadEntity={filters.reload}/>;
 */
export const FilterDrawerSimple = (props: IFilterDrawerSimpleProps) => {
    const { entity, filterTypeEntity, onReloadEntity, state, onChange, appliedFilterCount } = props;
    // When allSelected is true, we fill in the selectedValue with all of the options
    const correctedFilterValues = useMemo(() => {
        if (state.appliedFilter) {
            return state.appliedFilter.map((f) => {
                let selectedValue = f.selectedValue;
                if (f.allSelected) {
                    selectedValue = flattenTreeData(entity.options[f.key]).map((i) => i.value);
                }
                return { ...f, selectedValue };
            });
        }
        return state.appliedFilter;
    }, [state.appliedFilter, entity]);

    const showHasUnsavedChanges = useMemo(() => {
        return (
            !!correctedFilterValues &&
            appliedFiltersNotFromSavedFilter(
                correctedFilterValues,
                entity,
                entity.savedFilters.find((s) => s.id === state.selectedFilterId),
            )
        );
    }, [correctedFilterValues, entity, state.selectedFilterId]);

    const setFilterHistoryState = useCallback(
        (state: IFilterReducerState) => {
            const correctedValues: IFilterFormValues = applySerializeBehavior(entity, {
                items: state.appliedFilter!,
                savedFilter: entity.savedFilters.find((s) => s.id === state.selectedFilterId)!,
            });
            const newFilterState: IFilterHistoryStateWithType = {
                ...state,
                filterType: filterTypeEntity,
                appliedFilter: correctedValues.items,
            };
            onChange(newFilterState);
        },
        [filterTypeEntity, entity, onChange],
    );

    const setUnsavedAppliedFilter = useCallback(
        (items: SelectedFilterItem[], isChanged: boolean) => {
            if (isChanged) {
                setFilterHistoryState(
                    FilterReducer(
                        { ...state, showHasUnsavedChanges: false },
                        new SetUnsavedAppliedFilter(items, isChanged),
                    ),
                );
            }
        },
        [state, setFilterHistoryState],
    );

    const updateUnsavedChanges = useCallback(
        (
            newFilters: SelectedFilterItem[],
            filterEntity: FilterEntity,
            savedFilter: SavedFilterItem,
        ) => {
            setFilterHistoryState(
                FilterReducer(
                    { ...state, showHasUnsavedChanges: false },
                    new UpdateUnsavedChanges(newFilters, filterEntity, savedFilter, true),
                ),
            );
        },
        [setFilterHistoryState, state],
    );

    const handleSavedFiltersUpdated = useCallback(
        async (_1, id: number) => {
            await onReloadEntity(id);
        },
        [onReloadEntity],
    );

    return (
        <FilterDrawer
            {...props}
            // no longer need this prop to do anything
            onSubmit={async () => {}}
            // passing in updateUnsavedChanges opts out of using filter context
            updateUnsavedChanges={updateUnsavedChanges}
            appliedFilter={correctedFilterValues}
            setUnsavedAppliedFilter={setUnsavedAppliedFilter}
            showHasUnsavedChanges={showHasUnsavedChanges}
            appliedFiltersFromSavedFilter={state.appliedFiltersFromSavedFilter}
            selectedFilterId={state.selectedFilterId}
            shouldUpdateFilterValueContext={false}
            onSavedFiltersUpdated={handleSavedFiltersUpdated}
            appliedFilterCount={appliedFilterCount}
        />
    );
};

export const getFiltersFromHistory = (
    historyState: IFilterDrawerHistoryState | undefined,
    filterTypeEntity: FilterEntityType,
    initial: IFilterHistoryState,
): IFilterHistoryState => {
    if (historyState?.filterState && historyState?.filterState.filterType === filterTypeEntity) {
        return historyState.filterState;
    } else {
        return initial;
    }
};

/**
 * Use when filter button needs to appear before entity is loaded
 */
export const FilterDrawerWrapped: React.FC<{ filterProps?: IFilterProps }> = (props) => {
    if (!props.filterProps) {
        return (
            <BTButtonDeprecated type="secondary" data-testid="filters" icon={<BTIconFilter />}>
                Filter <FilterCountDisplay />
            </BTButtonDeprecated>
        );
    }
    return <FilterDrawer {...props.filterProps} />;
};
